import { LAYOUT_COMPONENT_TYPE, type LayoutBlock } from '../content/studio';
import type { PageMeta } from '../content/pageMeta';
import {
  HOMEPAGE_SLUG,
  buildHomepageAnchorHref,
  buildLocalizedSitePath,
  prefixPublicPath,
  publicPathForPageSlug,
  resolveSlugFromPath,
  stripLocalePrefixFromPathname,
} from '../routing/pageRouting';
import type { SiteMode } from '../routing/siteMode';
import { resolveMinimalSlugAnchor } from '../routing/siteMode';
import {
  collectNavTreeNodeIds,
  getNavTreeDefinition,
  type NavMenuLayout,
  type NavTarget,
  type NavTreeNodeDef,
} from './navTreeRegistry';

export const NAV_LINK_FIELD_KEYS = ['navLink1', 'navLink2', 'navLink3'] as const;

export type NavLinkFieldKey = (typeof NAV_LINK_FIELD_KEYS)[number];

/**
 * Legacy navLink1–3 were authored for Menu / About / Contact (minimal & early trees).
 * Map by node id so restaurant standard `[home, menu, about-page, contact]` does not steal Menu onto Home.
 */
const LEGACY_NAV_LINK_FIELD_BY_NODE_ID: Partial<Record<string, NavLinkFieldKey>> = {
  menu: 'navLink1',
  about: 'navLink2',
  'about-page': 'navLink2',
  contact: 'navLink3',
  omakase: 'navLink2',
};

export interface ResolvedNavigationNode {
  id: string;
  fieldKey: string;
  label: string;
  href: string;
  pageSlug?: string;
  layout: NavMenuLayout;
  children: ResolvedNavigationNode[];
}

function pickLocalized(
  record: Record<string, string> | undefined,
  locale: string,
  defaultLocale: string,
): string {
  if (!record) {
    return '';
  }
  return record[locale] ?? record[defaultLocale] ?? Object.values(record)[0] ?? '';
}

/** Maps a tree node to the block content key used for label overrides. */
export function navLabelFieldKey(nodeId: string): string {
  return `navLabel_${nodeId}`;
}

export function isNavLabelOverrideFieldKey(key: string): boolean {
  return (
    (NAV_LINK_FIELD_KEYS as readonly string[]).includes(key) || key.startsWith('navLabel_')
  );
}

/**
 * Editor overrides apply only for the active locale key.
 * Falling back to defaultLocale here pinned German "Start" onto every new language.
 */
function pickLabelOverride(
  content: LayoutBlock['content'],
  nodeId: string,
  locale: string,
  _defaultLocale: string,
): string {
  const byNode = content[navLabelFieldKey(nodeId)]?.[locale]?.trim() ?? '';
  if (byNode) {
    return byNode;
  }
  const legacyKey = LEGACY_NAV_LINK_FIELD_BY_NODE_ID[nodeId];
  if (legacyKey) {
    return content[legacyKey]?.[locale]?.trim() ?? '';
  }
  return '';
}

function labelFromPageTitle(
  pages: PageMeta[] | undefined,
  pageSlug: string | undefined,
  locale: string,
  defaultLocale: string,
): string {
  if (!pageSlug || !pages?.length) {
    return '';
  }
  const page = pages.find((entry) => entry.slug === pageSlug);
  if (!page) {
    return '';
  }
  const exact = page.localizedTitle?.[locale]?.trim();
  if (exact) {
    return exact;
  }
  return (
    pickLocalized(page.localizedTitle, locale, defaultLocale).trim() ||
    pickLocalized(page.localizedTitle, defaultLocale, defaultLocale).trim() ||
    page.slug
  );
}

function defaultLabelForNode(
  node: NavTreeNodeDef,
  locale: string,
  defaultLocale: string,
  pages: PageMeta[] | undefined,
  target: { href: string; pageSlug?: string },
): string {
  const labels = node.labels as Record<string, string | undefined>;
  // Exact registry hit (de/en/vi today) — keep starter chrome for those locales.
  const exactRegistry = labels[locale]?.trim();
  if (exactRegistry) {
    return exactRegistry;
  }

  // Other catalog locales: prefer DeepL / editor page titles over German registry fallback.
  if (target.pageSlug) {
    const exactPage = pages?.find((entry) => entry.slug === target.pageSlug)?.localizedTitle?.[
      locale
    ]?.trim();
    if (exactPage) {
      return exactPage;
    }
  }

  const registryFallback =
    labels[defaultLocale]?.trim() || labels.de?.trim() || '';
  if (registryFallback) {
    return registryFallback;
  }

  const fromPage = labelFromPageTitle(pages, target.pageSlug, locale, defaultLocale);
  if (fromPage) {
    return fromPage;
  }
  return node.id;
}

function resolveNavTargetHref(
  target: NavTarget,
  siteMode: SiteMode,
  locale: string,
  defaultLocale: string,
  publicPathPrefix?: string | null,
): { href: string; pageSlug?: string } {
  switch (target.kind) {
    case 'anchor':
      return {
        href: buildHomepageAnchorHref(target.anchor, locale, defaultLocale, publicPathPrefix),
      };
    case 'href':
      return { href: prefixPublicPath(target.href, publicPathPrefix) };
    case 'page': {
      if (siteMode === 'minimal' && target.slug !== HOMEPAGE_SLUG) {
        return {
          href: buildHomepageAnchorHref(
            resolveMinimalSlugAnchor(target.slug),
            locale,
            defaultLocale,
            publicPathPrefix,
          ),
          pageSlug: target.slug,
        };
      }
      return {
        href: prefixPublicPath(
          buildLocalizedSitePath(publicPathForPageSlug(target.slug), locale, defaultLocale),
          publicPathPrefix,
        ),
        pageSlug: target.slug,
      };
    }
    default: {
      const _exhaustive: never = target;
      return _exhaustive;
    }
  }
}

function resolveNode(
  node: NavTreeNodeDef,
  block: LayoutBlock,
  pages: PageMeta[] | undefined,
  locale: string,
  defaultLocale: string,
  siteMode: SiteMode,
  publicPathPrefix?: string | null,
): ResolvedNavigationNode | null {
  const fieldKey = navLabelFieldKey(node.id);
  const target = resolveNavTargetHref(
    node.target,
    siteMode,
    locale,
    defaultLocale,
    publicPathPrefix,
  );
  const labelOverride = pickLabelOverride(block.content, node.id, locale, defaultLocale);
  const label = labelOverride || defaultLabelForNode(node, locale, defaultLocale, pages, target);

  if (!label.trim()) {
    return null;
  }

  const children = (node.children ?? [])
    .map((child) =>
      resolveNode(child, block, pages, locale, defaultLocale, siteMode, publicPathPrefix),
    )
    .filter((child): child is ResolvedNavigationNode => child !== null);

  return {
    id: node.id,
    fieldKey,
    label,
    href: target.href,
    pageSlug: target.pageSlug,
    layout: node.layout ?? (children.length > 0 ? 'dropdown' : 'default'),
    children,
  };
}

/**
 * Resolves the dev-defined navigation tree with editor label overrides.
 * Link targets are never read from block content.
 */
export function resolveNavigationTree(
  block: LayoutBlock,
  input: {
    pages?: PageMeta[];
    locale: string;
    defaultLocale: string;
    siteMode?: SiteMode;
    vertical?: string;
    themeId?: string;
    /** Path-based preview prefix (`/api/serve-staging/{id}`). */
    publicPathPrefix?: string | null;
    /**
     * Site layout blocks — used to detect a tasting timeline when
     * `hasTastingTimeline` is not set explicitly (e.g. SSR full-site render).
     */
    siteBlocks?: LayoutBlock[];
    /** Prefer this over scanning `siteBlocks` (sandbox syncs a site-wide flag). */
    hasTastingTimeline?: boolean;
  },
): ResolvedNavigationNode[] {
  const siteMode = input.siteMode ?? 'standard';
  const hasTastingTimeline =
    input.hasTastingTimeline ??
    (input.siteBlocks ?? []).some(
      (entry) => entry.componentType === LAYOUT_COMPONENT_TYPE.tastingMenuCourseTimeline,
    );
  const tree = getNavTreeDefinition({
    vertical: input.vertical,
    siteMode,
    themeId: input.themeId,
    hasTastingTimeline,
  });

  return tree.nodes
    .map((node) =>
      resolveNode(
        node,
        block,
        input.pages,
        input.locale,
        input.defaultLocale,
        siteMode,
        input.publicPathPrefix,
      ),
    )
    .filter((node): node is ResolvedNavigationNode => node !== null);
}

/**
 * Page slug for the request the visitor is on.
 * Strips a path-based preview prefix (`/api/serve-staging/{id}`) and a locale segment.
 * `/` and `/{locale}` resolve to `index`.
 */
export function resolveNavCurrentPageSlug(input: {
  pathname?: string | null;
  publicPathPrefix?: string | null;
  supportedLocales?: readonly string[];
}): string | null {
  const raw = input.pathname?.trim();
  if (!raw) {
    return null;
  }

  let path = raw.split(/[?#]/)[0] ?? raw;
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }

  const prefix = input.publicPathPrefix?.trim().replace(/\/$/, '') ?? '';
  if (prefix && (path === prefix || path.startsWith(`${prefix}/`))) {
    path = path.slice(prefix.length) || '/';
  }
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }

  path = stripLocalePrefixFromPathname(path, input.supportedLocales ?? []);
  return resolveSlugFromPath(path);
}

/**
 * True when this node is the page being viewed.
 * Homepage section anchors (`/#menu`) keep a page slug in minimal mode but are not that page.
 */
export function navNodeIsCurrentPage(
  node: { pageSlug?: string; href: string },
  currentSlug: string | null,
): boolean {
  if (!currentSlug || !node.pageSlug || node.href.includes('#')) {
    return false;
  }
  return node.pageSlug === currentSlug;
}

/** All label override field keys for a vertical + site mode (editor forms). */
export function listNavLabelFieldKeys(input: {
  vertical?: string;
  siteMode: SiteMode;
  themeId?: string;
  hasTastingTimeline?: boolean;
}): Array<{ nodeId: string; fieldKey: string }> {
  const tree = getNavTreeDefinition(input);
  const result: Array<{ nodeId: string; fieldKey: string }> = [];

  const walk = (nodes: NavTreeNodeDef[]) => {
    for (const node of nodes) {
      result.push({
        nodeId: node.id,
        fieldKey: navLabelFieldKey(node.id),
      });
      if (node.children?.length) {
        walk(node.children);
      }
    }
  };

  walk(tree.nodes);
  return result;
}

export { collectNavTreeNodeIds };
